import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../api/config';
import type { Activation } from '../types';
import {
  Card,
  CardContent,
  Badge,
  PageHeader,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableSkeleton,
  EmptyState,
  Alert,
} from '@license-management/ui';
import { Monitor, AlertCircle } from 'lucide-react';

function ActivationsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['activations'],
    queryFn: () => apiClient.get<Activation[]>(API_ENDPOINTS.activations),
  });

  if (error) {
    return (
      <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
        Failed to load activations. Please try again later.
      </Alert>
    );
  }

  return (
    <div>
      <PageHeader
        title="Your Activations"
        description="All device activations across your licenses"
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>License Key</TableHead>
                  <TableHead>Machine ID</TableHead>
                  <TableHead>Activated</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableSkeleton rows={5} columns={5} />
              </TableBody>
            </Table>
          ) : data?.length === 0 ? (
            <EmptyState
              icon={<Monitor className="h-12 w-12" />}
              title="No activations found"
              description="Your licenses haven't been activated on any devices yet."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>License Key</TableHead>
                  <TableHead>Machine ID</TableHead>
                  <TableHead>Activated</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.map((activation) => (
                  <TableRow key={activation.activationId}>
                    <TableCell>
                      <Link
                        to={`/licenses/${activation.licenseId}`}
                        className="font-mono text-sm text-primary-600 hover:text-primary-700"
                      >
                        {activation.licenseKey}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-gray-500 text-xs">
                      {activation.machineId.substring(0, 16)}...
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {new Date(activation.activatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {new Date(activation.lastSeenAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          activation.status === 'active' ? 'success' : 'error'
                        }
                      >
                        {activation.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ActivationsPage;
